import { ListMeta } from "./common";

export interface Demo {
  id: string;
  attachment: string;
  attachment_url: string;
  file_name: string;
  status: number;
  additional_info: string;
  video_analysis: DemoAnalysis;
  created_at: string;
  updated_at: string;
}

export interface DemoAnalysis {
  video_metadata: {
    file_name: string;
    word_count: number;
    chapter_count: number;
    speaker_count: number;
    sentence_count: number;
    duration_seconds: number;
    duration_formatted: string;
    transcription_confidence: number;
  };
  product_analysis: {
    use_cases: Array<{
      scenario: string;
      target_users: string[];
    }>;
    target_audience: string[];
    unique_features: Array<{
      name: string;
      description: string;
    }>;
    core_pain_points: string[];
    additional_insights: string[];
    product_description: string;
  };
  full_video_analysis: string;
  comprehensive_text_report: string;
}

export interface DemoDetails {
  id: string;
  attachment: string;
  attachment_url: string;
  file_name: string;
  status: number;
  additional_info: string;
  video_analysis: DemoAnalysis;
  created_at: string;
  updated_at: string;
}

export interface DemoUploadResponse {
  id: string;
  filename: string;
  attachment_url: string;
  status: 'Processing';
  message: string;
}

export interface DemoListResponse {
  data: Demo[];
  metadata: ListMeta;
} 

export interface DemoStats {
  total_demos: number;
  completed_count: number;
  key_features_identified: number;
  avg_analysis_time: number;
}

export enum DemoStatus {
  NOTIFICATION_RECEIVED = 0,
  PROCESSING = 1,
  PROCESSED = 2,
  FAILED = 3,
  INITIALIZED = 4,
  REINDEXING = 5,
}

export interface ProductDemosPageProps {
  demos: Demo[];
  listMetaData: ListMeta;
  demosLoading: boolean;
  demosError: string;
  demoDetailsLoading: boolean;
  demoDetailsError: string;
  uploadLoading: boolean;
  uploadError: string;
  showDemoModal: boolean;
  selectedDemo: Demo | null;
  demoDetails: DemoDetails | null;
  updateAdditionalContextLoading: boolean;
  updateAdditionalContextError: string;
  removeDemoLoading: boolean;
  removeDemoError: string;
  stats: DemoStats;
  getDemos: (page?: number, pageSize?: number) => void;
  getDemoDetails: (videoId: string) => void;
  uploadDemo: (file: File) => void;
  toggleDemoModal: (show: boolean) => void;
  setSelectedDemo: (demo: Demo | null) => void;
  updateAdditionalContext: (videoId: string, content: string) => void;
  getStats: () => void;
  removeDemo: (videoId: string) => void;
  updateCurrentStatus: (videoId: string) => void;
  syncDemoStatus: () => void;
}

export interface DemoLibraryProps {
  demos: Demo[];
  demosLoading: boolean;
  listMetaData: any;
  currentPage: number;
  pageSize: number;
  removeDemoLoading: boolean;
  removeDemoError: string;
  onRemove: (videoId: string) => void;
  onCardClick: (demo: Demo) => void;
  onPageChange: (page: number, size?: number) => void;
  onUploadClick: () => void;
  updateCurrentStatus: (videoId: string) => void;
}

export interface DemoStatsProps {
  stats: {
      total_demos: number;
      completed_count: number;
      key_features_identified: number;
      avg_analysis_time: number;
  };
}

export interface DemoDetailsModalProps {
  open: boolean;
  onCancel: () => void;
  selectedDemo: Demo | null;
  demoDetails: any;
  demoDetailsLoading: boolean;
  updateAdditionalContextLoading: boolean;
  onUpdateAdditionalContext: (demoId: string, context: string) => void;
}

export interface UploadModalProps {
  open: boolean;
  onCancel: () => void;
  onUpload: (file: File) => Promise<void>;
  loading: boolean;
}

export interface VideoCardProps {
  id: string;
  title: string;
  date: string;
  time: string;
  status: number;
  description: string;
  attachment: string;
  removeDemoLoading: boolean;
  removeDemoError: string;
  updateCurrentStatus: (videoId: string) => void;
  onClick: () => void;
  onRemove: () => void;
}

export interface BadgeProps {
  label: string;
  color: string;
  bg: string;
}

export interface StatCardProps {
  label: string;
  value: string;
}